import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  grammarImportFileSchema,
  importFileSchema,
  quizImportFileSchema,
  scenarioImportFileSchema,
} from "@rmnlng/shared";
import { apiFetch } from "./api";

export type ImportKind = "words" | "grammar" | "quiz" | "scenarios";

export type ImportResult = {
  added: number;
  updated: number;
  total: number;
  errors: Array<{ index: number; message: string }>;
  // Non-fatal authoring issues — surfaced in the UI as a "X warning"
  // suffix on the success row. Used by grammar (unknown
  // `relatedRuleSlugs`) and scenarios (unknown lemma / rule slug /
  // prerequisite scenario). Optional so older endpoints that don't
  // emit this field still parse cleanly.
  warnings?: Array<{ message: string }>;
};

/**
 * Parse a File as JSON and validate against the shared Zod schema.
 * Returns either the parsed payload or a readable client-side error — so
 * we can show "this JSON is malformed" without round-tripping to the server.
 */
export async function parseImportFile(
  file: File,
  kind: ImportKind,
): Promise<{ ok: true; payload: unknown } | { ok: false; error: string }> {
  let text: string;
  try {
    text = await file.text();
  } catch {
    return { ok: false, error: "Could not read file" };
  }

  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (err) {
    return {
      ok: false,
      error: `Not valid JSON: ${err instanceof Error ? err.message : "parse error"}`,
    };
  }

  const schema =
    kind === "words"
      ? importFileSchema
      : kind === "grammar"
        ? grammarImportFileSchema
        : kind === "quiz"
          ? quizImportFileSchema
          : scenarioImportFileSchema;
  const result = schema.safeParse(json);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join(".") || "(root)";
    return { ok: false, error: `${path}: ${first?.message ?? "schema mismatch"}` };
  }
  return { ok: true, payload: result.data };
}

/**
 * Top-level cache keys that an import can touch. Used by every
 * `useImport*` hook below to invalidate stale data after a successful
 * upsert — without this, an open GrammarDetail / WordDetail page
 * keeps showing the pre-import body indefinitely (React Query holds
 * the cache until a refetch trigger fires, which on a SPA basically
 * means never).
 *
 * Cast through `readonly unknown[]` so the call sites can pass each
 * one as a `queryKey` without TS narrowing to the literal tuple.
 */
const ROOT_KEY = {
  words: ["words"] as const,
  grammar: ["grammar"] as const,
  // Quiz upserts change rule-coverage state (rules can flip from
  // "no quizzes" → "has quizzes"), and any new items show up in
  // the daily queue → both caches need a refresh.
  reviews: ["reviews"] as const,
  // Progress views (rule coverage, level rings) derive from word /
  // grammar / quiz state, so any import can shift them.
  progress: ["progress"] as const,
} as const;

/**
 * Invalidate a set of top-level cache keys. `qc.invalidateQueries`
 * with a parent prefix bumps every nested key (lists + details).
 */
function invalidate(qc: ReturnType<typeof useQueryClient>, keys: readonly (readonly string[])[]) {
  for (const key of keys) {
    qc.invalidateQueries({ queryKey: key });
  }
}

export function useImportWords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: unknown) =>
      apiFetch<ImportResult>("/admin/import/words", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      // Word imports also touch the auto-matched grammar rules (a new
      // word can flip a rule's "Applies to" coverage), so grammar +
      // progress get invalidated alongside the obvious `words` keys.
      invalidate(qc, [ROOT_KEY.words, ROOT_KEY.grammar, ROOT_KEY.reviews, ROOT_KEY.progress]);
    },
  });
}

export function useImportGrammar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: unknown) =>
      apiFetch<ImportResult>("/admin/import/grammar", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      // Grammar imports overwrite the rule body — invalidating the
      // grammar root forces GrammarDetail to refetch on its next
      // render rather than serving the cached pre-import body
      // (the bug this hook now fixes).
      invalidate(qc, [ROOT_KEY.grammar, ROOT_KEY.words, ROOT_KEY.reviews, ROOT_KEY.progress]);
    },
  });
}

export function useImportQuiz() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: unknown) =>
      apiFetch<ImportResult>("/admin/import/quiz", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      // Quiz changes affect rule state (coverage flips) + the daily
      // queue (new items become candidates) + progress rings.
      invalidate(qc, [ROOT_KEY.grammar, ROOT_KEY.reviews, ROOT_KEY.progress]);
    },
  });
}

export function useImportScenarios() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: unknown) =>
      apiFetch<ImportResult>("/admin/import/scenarios", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      // Scenarios cross-link words + grammar; safest to refresh both
      // along with progress (scenario-coverage signals are folded
      // into the level-up readiness check).
      invalidate(qc, [ROOT_KEY.words, ROOT_KEY.grammar, ROOT_KEY.reviews, ROOT_KEY.progress]);
    },
  });
}

/**
 * Admin debug affordance: set `currentLevel` to the chosen level and
 * delete all the admin's own LevelUpAttempt rows (cascades to
 * LevelUpAttemptAnswer). Used from Settings to retake an exam without
 * waiting for the 24h cooldown — testing flow only.
 *
 * Invalidates `auth/me` (drives the Home + Settings reads),
 * `progress/levels` (drives the Levels page rings + readyForExam
 * gate), and `reviews/queue` (the queue scope follows currentLevel).
 */
export function useAdminResetToLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (level: string) =>
      apiFetch<{ currentLevel: string; deletedAttempts: number }>(`/admin/reset-to-level/${level}`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["auth", "me"] });
      void qc.invalidateQueries({ queryKey: ["progress"] });
      void qc.invalidateQueries({ queryKey: ["reviews"] });
    },
  });
}

/**
 * Bounded-concurrency runner — caps in-flight work at `limit`. Used by
 * the Admin page to upload many JSON files without blowing past the
 * browser's per-origin connection cap or thundering-herding the API
 * (each import does many DB round-trips, including shared-tag upserts
 * that contend on row locks under heavy parallel load).
 *
 * The worker function is responsible for swallowing per-item errors
 * (e.g. updating UI state) — this loop never sees a rejection.
 */
export async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  const workerCount = Math.max(1, Math.min(limit, items.length));
  const workers = Array.from({ length: workerCount }, async () => {
    while (cursor < items.length) {
      const i = cursor++;
      await worker(items[i]!, i);
    }
  });
  await Promise.all(workers);
}
